import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Clock } from "lucide-react";
import { api, GEN } from "../lib/api";

export const CharactersSection = ({ onJoin }) => {
  const [chars, setChars] = useState([]);

  useEffect(() => {
    api.get("/characters").then(({ data }) => setChars(data)).catch(() => {});
  }, []);

  const hero = chars.find((c) => c.id === "naijaman");
  const rest = chars.filter((c) => c.id !== "naijaman");

  return (
    <section id="characters" className="relative py-24 sm:py-32 bg-black overflow-hidden" data-testid="characters-section">
      <div className="halftone absolute inset-0 opacity-20" />
      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="font-accent text-xl tracking-[0.3em] text-emerald-400">THE ROSTER</p>
          <h2 className="font-display text-5xl sm:text-7xl text-white mt-1">CHARACTERS</h2>
        </motion.div>

        {/* Featured hero */}
        {hero && (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="grid lg:grid-cols-2 gap-8 items-center rounded-2xl overflow-hidden border border-emerald-500/20 bg-gradient-to-br from-emerald-950/30 to-black mb-10"
          >
            <div className="relative h-[420px] lg:h-[560px]">
              <img src={GEN.closeup} alt={hero.name} className="w-full h-full object-cover object-top" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 lg:to-[#03130b]" />
            </div>
            <div className="p-8 lg:p-12">
              <p className="font-accent text-lg tracking-[0.3em] text-amber-500">{hero.alias}</p>
              <h3 className="font-display text-5xl sm:text-6xl text-white text-glow-green">{hero.name}</h3>
              <p className="text-gray-300 mt-4 leading-relaxed">{hero.bio}</p>
              <div className="mt-6">
                <p className="font-accent text-lg tracking-widest text-gray-400 mb-3">POWERS</p>
                <div className="flex flex-wrap gap-2">
                  {hero.powers.map((p) => (
                    <span key={p} className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm px-3 py-1.5 rounded-full">
                      <Zap size={13} /> {p}
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={onJoin} className="btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide px-7 py-3 mt-8 glow-green transition-transform hover:scale-105" data-testid="character-join-btn">
                <span>Join NAIJAMAN&rsquo;s Universe</span>
              </button>
            </div>
          </motion.div>
        )}

        {/* Grid of others */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {rest.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ rotate: c.comingSoon ? 0 : -1, scale: 1.02 }}
              className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-amber-500/50 bg-white/5 aspect-[3/4] transition-colors duration-300"
              data-testid={`character-card-${c.id}`}
            >
              {c.id === "badbellus" ? (
                <img src={GEN.villain} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
              ) : c.image ? (
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-950/40 to-black grid place-items-center">
                  <span className="font-display text-6xl text-white/10">N</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              {c.comingSoon && (
                <div className="absolute inset-0 grid place-items-center">
                  <span className="flex items-center gap-2 bg-black/70 backdrop-blur border border-white/20 text-white font-accent text-lg tracking-widest px-4 py-2 rounded-full">
                    <Clock size={16} /> COMING SOON
                  </span>
                </div>
              )}
              <div className="absolute bottom-0 p-4">
                <p className={`font-display text-2xl ${c.status === "villain" ? "text-purple-400" : "text-white"}`}>{c.name}</p>
                <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{c.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
